import React, { useEffect, useState, useMemo } from "react";
import { View, ScrollView, RegisteredStyle, ViewStyle, Platform, StyleSheet, useWindowDimensions } from "react-native";
import ShareCommentBox from "../components/ShareCommentBox";
import ShareLookPicker from "../components/ShareLookPicker";
import SharePreviewCard from "../components/SharePreviewCard";
import ShareWrapper from "../../../components/ShareWrapper";
import ShareButton, { ShareButtonProps } from "../components/ShareButton";
import { useModalStore } from "../../../store/modalStore";
import getGlobalStyles from "../../../globalStyles";
import { ROUTES } from "../../../shared/constants/routes";
import { GRADIENT_TYPE } from "../../../components/CustomLinearGradient";
import { useArticlesData } from "../../../providers/ArticlesDataProvider";
import { Article } from "../../../shared/types/DataTypes";
import { useKeyboardHeight } from "../../../hooks/useKeyboardHeight";
import { useTranslation } from "react-i18next";

const ShareScreen = () => {
  const { t } = useTranslation();
  const gStyles = getGlobalStyles();
  const shareProps: any = useModalStore((state) => state.modalAdditionalProps);
  const { articles: allArticles } = useArticlesData();

  const [comment, setComment] = useState("");
  const [selectedLook, setSelectedLook] = useState(GRADIENT_TYPE.CARD2);
  const [articles, setArticles] = useState<Article[]>([]);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const keyboardHeight = useKeyboardHeight(); // native height for iOS/Android
  const { height: SCREEN_HEIGHT } = useWindowDimensions();

  // Load articles
  useEffect(() => {
    if (shareProps?.shareCollection) {
      setArticles(allArticles.slice(0, 3));
    } else if (shareProps?.article) {
      setArticles([shareProps.article]);
    } else if (shareProps?.video) {
      const v = shareProps.video;
      const thumb = v.videoId
        ? `https://i.ytimg.com/vi/${v.videoId}/maxresdefault.jpg`
        : v.thumbnail || '';
      setArticles([{ id: v.videoId, title: v.title, image: thumb } as unknown as Article]);
    }
  }, [shareProps?.article, shareProps?.collection, shareProps?.video, allArticles]);

  const getShareButtonProps = (view: React.LegacyRef<View>): ShareButtonProps => {
    if (shareProps?.video) {
      return {
        viewRef: view,
        title: Platform.OS === 'web' ? `${t('screens.share.shareMessage')} -` : `${t('screens.share.shareMessage')} ${shareProps.video.title}`,
        summary: '',
        deepLink: `${ROUTES.HOME}?selectedTab=${ROUTES.WATCH}&videoId=${shareProps.video.videoId}`,
      };
    }
    if (shareProps?.collection && !shareProps?.article) {
      return {
        viewRef: view,
        title: Platform.OS === 'web' ? `${t('screens.share.shareMessage')} -` : `${t('screens.share.shareMessageCollection')} ${shareProps?.collection?.title}`,
        summary: shareProps?.collection?.description || '',
        deepLink: `${ROUTES.HOME}?selectedTab=${shareProps.sharedFrom}&collectionId=${shareProps?.collection?.id}`
      }
    } else {
      let deepLink = `${ROUTES.IMMERSRSIVE_NEWS_ARTICLE}?articleId=${shareProps?.article?.id!}`;
      if (shareProps.sharedFrom === ROUTES.LISTEN || shareProps.sharedFrom === ROUTES.RADIO_PLAYER) {
        deepLink = `${ROUTES.RADIO_PLAYER}?articleId=${shareProps?.article?.id!}`
      }
      return {
        viewRef: view,
        title: Platform.OS === 'web' ? `${t('screens.share.shareMessage')} -` : `${t('screens.share.shareMessage')} ${shareProps?.article?.title}`,
        summary: shareProps?.article?.gistContent || shareProps?.article?.content || '',
        deepLink: `${deepLink}&collectionId=${shareProps?.collection?.id}`
      }
    }
  }

  const scrollContentStyle = useMemo(() => [
    styles.container,
    { paddingBottom: keyboardHeight || (isInputFocused && Platform.OS !== 'web') ? 300 : 0 },
  ], [keyboardHeight, isInputFocused]);

  return (
    <View style={[styles.safeArea, gStyles.fullWidth, {maxHeight: SCREEN_HEIGHT - (Platform.OS !== 'web' ? 100 : isInputFocused ? 10 : 175)}]}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[scrollContentStyle, {paddingBottom : keyboardHeight > 0 ? keyboardHeight + 150 : 120}]}>
        <ShareCommentBox
          value={comment}
          onChange={setComment}
          onFocusChange={setIsInputFocused}
        />

        <ShareLookPicker
          selected={selectedLook}
          onSelect={setSelectedLook}
        />

        <ShareWrapper dependencies={[comment, selectedLook, articles]}>
          {(viewRef: any) => (
            <>
              <ShareButton
                style={{ marginVertical: 20 } as unknown as RegisteredStyle<ViewStyle>}
                {...getShareButtonProps(viewRef)}
                comment={comment}
              />
              {articles.length > 0 && (
                <View ref={viewRef} collapsable={false}>
                  <SharePreviewCard
                    articles={articles}
                    collectionName={shareProps?.shareCollection && (shareProps?.collection?.title ?? '')}
                    comment={comment?.trim()}
                    look={selectedLook}
                  />
                </View>
              )}
            </>
          )}
        </ShareWrapper>
      </ScrollView>
    </View>
  );
};

export default ShareScreen;

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  container: { padding: 20 },
});